<template>
    <div class="manage-provider-page">
        <nav class="navbar navbar-top navbar-expand-md bg-white shadow-sm border-bottom" id="navbar-main">
            <div class="container-fluid">
                <div>
                    <p class="text-uppercase text-muted mb-1 super-page-kicker">Configuration</p>
                    <h2 class="h3 mb-0">{{ isEdit ? 'Edit Service Provider' : 'Add Service Provider' }}</h2>
                </div>
                <div class="text-muted small">
                    Provider setup and nested user access
                </div>
            </div>
        </nav>

        <div class="container-fluid mt-0 pt-5 pt-md-8">
            <div class="row justify-content-center">
                <div class="col-12 col-xl-10">
                    <div v-if="validationMessage" class="alert alert-warning border-0 shadow-sm">
                        {{ validationMessage }}
                    </div>

                    <b-card class="mb-4 shadow-sm border-0 provider-form-card">
                        <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-start mb-4">
                            <div class="mb-3 mb-lg-0">
                                <p class="super-page-kicker mb-2">Provider Info</p>
                                <h3 class="mb-2">Core provider settings</h3>
                                <p class="text-muted mb-0">
                                    Keep the provider name and meter allowance in sync before updating user access.
                                </p>
                            </div>
                            <span class="badge badge-pill badge-light provider-summary-badge">
                                {{ form.users.length }} configured {{ form.users.length === 1 ? 'user' : 'users' }}
                            </span>
                        </div>

                        <div class="row">
                            <div class="col-lg-7">
                                <b-form-group label="Provider Name" label-for="providerName">
                                    <b-form-input id="providerName" v-model.trim="form.name"
                                        placeholder="Enter provider name" :class="{ 'is-invalid': error.name }">
                                    </b-form-input>
                                </b-form-group>
                            </div>
                            <div class="col-lg-5">
                                <b-form-group label="Meter Allowance" label-for="meterAllowance">
                                    <b-form-input id="meterAllowance" type="number" min="0"
                                        v-model="form.meter_allowance" placeholder="Enter meter allowance"
                                        :class="{ 'is-invalid': error.meter_allowance }">
                                    </b-form-input>
                                </b-form-group>
                            </div>
                        </div>
                    </b-card>

                    <!-- Preferences card -->
                    <b-card class="mb-4 shadow-sm border-0 provider-form-card">
                        <div class="mb-4">
                            <p class="super-page-kicker mb-2">Display Preferences</p>
                            <h3 class="mb-2">Billing &amp; formatting</h3>
                            <p class="text-muted mb-0">Controls labels, units, and currency shown to admin users for this provider.</p>
                        </div>

                        <div class="row">
                            <div class="col-lg-6">
                                <b-form-group label="Water utility label" label-for="prefWaterLabel"
                                    description="Shown wherever the water utility is referenced (e.g. CWA, LWSC)">
                                    <b-form-input id="prefWaterLabel" v-model.trim="form.preferences.water_label"
                                        placeholder="e.g. CWA"></b-form-input>
                                </b-form-group>
                            </div>
                            <div class="col-lg-6">
                                <b-form-group label="Electricity utility label" label-for="prefElecLabel"
                                    description="Shown wherever the electricity utility is referenced (e.g. CEB, KPLC)">
                                    <b-form-input id="prefElecLabel" v-model.trim="form.preferences.electricity_label"
                                        placeholder="e.g. CEB"></b-form-input>
                                </b-form-group>
                            </div>
                        </div>

                        <div class="row">
                            <div class="col-lg-4">
                                <b-form-group label="Water unit" label-for="prefWaterUnit"
                                    description="Meter reading unit for water (use m3 to display as m³)">
                                    <b-form-input id="prefWaterUnit" v-model.trim="form.preferences.water_unit"
                                        placeholder="e.g. m3"></b-form-input>
                                </b-form-group>
                            </div>
                            <div class="col-lg-4">
                                <b-form-group label="Electricity unit" label-for="prefElecUnit"
                                    description="Meter reading unit for electricity">
                                    <b-form-input id="prefElecUnit" v-model.trim="form.preferences.electricity_unit"
                                        placeholder="e.g. KWH"></b-form-input>
                                </b-form-group>
                            </div>
                            <div class="col-lg-4">
                                <b-form-group label="Gas unit" label-for="prefGasUnit"
                                    description="Meter reading unit for gas (billing consumption is always KG)">
                                    <b-form-input id="prefGasUnit" v-model.trim="form.preferences.gas_unit"
                                        placeholder="e.g. m3"></b-form-input>
                                </b-form-group>
                            </div>
                        </div>

                        <div class="row">
                            <div class="col-lg-4">
                                <b-form-group label="Currency symbol" label-for="prefCurrency"
                                    description="Symbol or code displayed on bills (e.g. Rs., $, €, KES)">
                                    <b-form-input id="prefCurrency" v-model.trim="form.preferences.currency"
                                        placeholder="e.g. Rs."></b-form-input>
                                </b-form-group>
                            </div>
                            <div class="col-lg-4">
                                <b-form-group label="Currency position" label-for="prefCurrencyPos">
                                    <b-form-select id="prefCurrencyPos" v-model="form.preferences.currency_position">
                                        <option value="before">Before amount — Rs. 1,200.00</option>
                                        <option value="after">After amount — 1,200.00 Rs.</option>
                                    </b-form-select>
                                </b-form-group>
                            </div>
                            <div class="col-lg-4">
                                <b-form-group label="Date format" label-for="prefDateFormat"
                                    :description="'PHP date() format. Default d M Y → ' + dateFormatExample">
                                    <b-form-input id="prefDateFormat" v-model.trim="form.preferences.date_format"
                                        placeholder="e.g. d M Y"></b-form-input>
                                </b-form-group>
                            </div>
                        </div>

                        <div class="row">
                            <div class="col-lg-6">
                                <b-form-group label="Signatory name" label-for="prefSignatory"
                                    description="Name printed as signatory on bill emails (e.g. The Management)">
                                    <b-form-input id="prefSignatory" v-model.trim="form.preferences.signatory_name"
                                        placeholder="e.g. The Syndic"></b-form-input>
                                </b-form-group>
                            </div>
                            <div class="col-lg-6">
                                <b-form-group label="Bill email languages"
                                    description="Language sections included in bill emails, in the order shown">
                                    <b-form-checkbox-group
                                        v-model="form.preferences.email_languages"
                                        :options="emailLanguageOptions"
                                        class="mt-1">
                                    </b-form-checkbox-group>
                                </b-form-group>
                            </div>
                        </div>

                        <hr class="my-3">

                        <div class="row">
                            <div class="col-lg-8">
                                <b-form-group label="Timezone" label-for="prefTzSearch"
                                    description="All dates and times in the admin app will display in this timezone">
                                    <b-form-input
                                        id="prefTzSearch"
                                        v-model="tzSearch"
                                        placeholder="Search timezones…"
                                        class="mb-2"
                                        autocomplete="off"
                                        :disabled="timezones.length === 0"
                                    />
                                    <b-form-select
                                        v-model="form.preferences.timezone"
                                        :options="filteredTimezones"
                                        :select-size="6"
                                        :disabled="timezones.length === 0"
                                    />
                                    <small v-if="form.preferences.timezone" class="text-muted mt-1 d-block">
                                        Selected: <strong>{{ form.preferences.timezone }}</strong>
                                    </small>
                                </b-form-group>
                            </div>
                            <div class="col-lg-4">
                                <b-form-group label="Scheduled email delivery hour"
                                    description="Hour in the tenant's timezone at which billing emails are sent">
                                    <b-form-select
                                        v-model="form.preferences.delivery_hour"
                                        :options="hourOptions"
                                    />
                                    <small class="text-muted mt-2 d-block">
                                        Emails sent at <strong>{{ formatHour(form.preferences.delivery_hour) }}</strong>.
                                    </small>
                                </b-form-group>
                            </div>
                        </div>
                    </b-card>

                    <!-- Accounting (Vilogi) card -->
                    <b-card class="mb-4 shadow-sm border-0 provider-form-card">
                        <div class="mb-4">
                            <p class="super-page-kicker mb-2">Integrations</p>
                            <h3 class="mb-2">Accounting (Vilogi)</h3>
                            <p class="text-muted mb-0">
                                Controls whether this provider's bills sync to Vilogi accounting. The API key is
                                never shown again once saved — leave it blank to keep the existing key.
                            </p>
                        </div>

                        <div class="row align-items-end">
                            <div class="col-lg-8">
                                <b-form-group label="Vilogi API Key" label-for="vilogiApiKey"
                                    :description="vilogiConfigured
                                        ? 'A key is already configured. Leave blank to keep it, or enter a new one to rotate it.'
                                        : 'No key configured yet for this provider.'">
                                    <b-form-input id="vilogiApiKey" type="password" v-model.trim="form.vilogi_api_key"
                                        autocomplete="new-password"
                                        :placeholder="vilogiConfigured ? '••••••••••' : 'Enter Vilogi API key'">
                                    </b-form-input>
                                </b-form-group>
                            </div>
                            <div class="col-lg-4 mb-3">
                                <b-button v-if="isEdit && vilogiConfigured" variant="outline-danger" block
                                    :disabled="removingVilogiConfig" @click="removeVilogiConfig">
                                    <b-spinner v-if="removingVilogiConfig" small class="mr-1"></b-spinner>
                                    {{ removingVilogiConfig ? 'Removing...' : 'Remove Configuration' }}
                                </b-button>
                            </div>
                        </div>

                        <div class="modal-user-status">
                            <div>
                                <p class="mb-1 font-weight-semibold">Vilogi accounting sync</p>
                                <p class="text-muted mb-0">
                                    Also gates whether bill emails and the mail cron attempt to sync to accounting —
                                    not just this page.
                                </p>
                            </div>
                            <b-form-checkbox switch v-model="form.allow_vilogi_webhook">
                                {{ form.allow_vilogi_webhook ? 'Enabled' : 'Disabled' }}
                            </b-form-checkbox>
                        </div>
                    </b-card>

                    <b-card class="mb-4 shadow-sm border-0 users-section-card">
                        <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center mb-4">
                            <div class="mb-3 mb-lg-0">
                                <p class="super-page-kicker mb-2">Users</p>
                                <h3 class="mb-2">Manage provider access</h3>
                                <p class="text-muted mb-0">
                                    Edit users in a modal, disable access with the switch, and keep disabled users muted.
                                </p>
                            </div>
                            <div class="d-flex align-items-center flex-wrap">
                                <span class="badge badge-pill badge-primary mr-2">{{ adminUserEntries.length }} admins</span>
                                <span class="badge badge-pill badge-info mr-2">{{ surveyorUserEntries.length }} surveyors</span>
                                <span class="badge badge-pill badge-secondary mr-3">{{ disabledUserEntries.length }} disabled</span>
                                <b-button variant="primary" @click="openUserModal()">
                                    <i class="fas fa-user-plus mr-2"></i>
                                    Add User
                                </b-button>
                            </div>
                        </div>

                        <b-tabs pills nav-class="user-status-tabs" content-class="pt-4">
                            <b-tab :title="`Admins (${adminUserEntries.length})`" active>
                                <div v-if="adminUserEntries.length" class="user-list-grid">
                                    <div v-for="entry in adminUserEntries" :key="entry.user.id || `admin-${entry.index}`"
                                        class="user-card" :class="{ 'is-disabled': entry.user.status !== 1 }">
                                        <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center">
                                            <div class="d-flex align-items-center mb-3 mb-lg-0">
                                                <div class="user-avatar" :class="{ muted: entry.user.status !== 1 }">
                                                    {{ userInitial(entry.user) }}
                                                </div>
                                                <div>
                                                    <h5 class="mb-1">{{ entry.user.name || 'Unnamed user' }}</h5>
                                                    <p class="text-muted mb-0">{{ entry.user.email }}</p>
                                                    <small class="user-role-label">{{ roleLabel(entry.user.role_id) }}</small>
                                                </div>
                                            </div>

                                            <div class="d-flex align-items-center flex-wrap user-card-actions">
                                                <span :class="entry.user.status === 1 ? 'badge badge-success mr-3' : 'badge badge-light mr-3'">
                                                    {{ entry.user.status === 1 ? 'Active' : 'Disabled' }}
                                                </span>
                                                <b-form-checkbox switch v-model="entry.user.status" :value="1"
                                                    :unchecked-value="0" class="mr-3 user-switch">
                                                    Enabled
                                                </b-form-checkbox>
                                                <b-button size="sm" variant="outline-primary"
                                                    @click="openUserModal(entry.index)">
                                                    Edit
                                                </b-button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div v-else class="user-empty-state">
                                    <h4 class="mb-2">No admin users</h4>
                                    <p class="text-muted mb-0">Add an admin user to manage provider operations.</p>
                                </div>
                            </b-tab>

                            <b-tab :title="`Surveyors (${surveyorUserEntries.length})`">
                                <div v-if="surveyorUserEntries.length" class="user-list-grid">
                                    <div v-for="entry in surveyorUserEntries"
                                        :key="entry.user.id || `surveyor-${entry.index}`" class="user-card"
                                        :class="{ 'is-disabled': entry.user.status !== 1 }">
                                        <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center">
                                            <div class="d-flex align-items-center mb-3 mb-lg-0">
                                                <div class="user-avatar" :class="{ muted: entry.user.status !== 1 }">
                                                    {{ userInitial(entry.user) }}
                                                </div>
                                                <div>
                                                    <h5 class="mb-1">{{ entry.user.name || 'Unnamed user' }}</h5>
                                                    <p class="text-muted mb-0">{{ entry.user.email }}</p>
                                                    <small class="user-role-label">{{ roleLabel(entry.user.role_id) }}</small>
                                                </div>
                                            </div>

                                            <div class="d-flex align-items-center flex-wrap user-card-actions">
                                                <span :class="entry.user.status === 1 ? 'badge badge-success mr-3' : 'badge badge-light mr-3'">
                                                    {{ entry.user.status === 1 ? 'Active' : 'Disabled' }}
                                                </span>
                                                <b-form-checkbox switch v-model="entry.user.status" :value="1"
                                                    :unchecked-value="0" class="mr-3 user-switch">
                                                    Enabled
                                                </b-form-checkbox>
                                                <b-button size="sm" variant="outline-primary"
                                                    @click="openUserModal(entry.index)">
                                                    Edit
                                                </b-button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div v-else class="user-empty-state">
                                    <h4 class="mb-2">No surveyor users</h4>
                                    <p class="text-muted mb-0">Add a surveyor user for meter reading operations.</p>
                                </div>
                            </b-tab>
                        </b-tabs>
                    </b-card>

                    <div class="mt-4 d-flex justify-content-end">
                        <b-button variant="secondary" @click="$router.back()">Cancel</b-button>
                        <b-button variant="primary" class="ml-2" @click="submitForm">Save</b-button>
                    </div>
                </div>
            </div>
        </div>

        <b-modal ref="userModal" :title="userEditor.index === null ? 'Add User' : 'Edit User'" hide-footer centered
            @hidden="resetUserEditor">
            <div v-if="userModalError.message" class="alert alert-warning">
                {{ userModalError.message }}
            </div>

            <b-form-group label="Full Name" label-for="userModalName">
                <b-form-input id="userModalName" v-model.trim="userEditor.form.name" placeholder="Enter full name"
                    :class="{ 'is-invalid': userModalError.name }">
                </b-form-input>
            </b-form-group>

            <b-form-group label="Email" label-for="userModalEmail">
                <b-form-input id="userModalEmail" v-model.trim="userEditor.form.email" placeholder="Enter email"
                    :class="{ 'is-invalid': userModalError.email }">
                </b-form-input>
            </b-form-group>

            <b-form-group label="User Type" label-for="userModalRole">
                <b-form-select id="userModalRole" v-model="userEditor.form.role_id" :options="roleOptions"
                    :class="{ 'is-invalid': userModalError.role_id }">
                </b-form-select>
            </b-form-group>

            <div class="modal-user-status">
                <div>
                    <p class="mb-1 font-weight-semibold">Access Status</p>
                    <p class="text-muted mb-0">Disabled users stay attached to the provider but cannot use the account.</p>
                </div>
                <b-form-checkbox switch v-model="userEditor.form.status" :value="1" :unchecked-value="0">
                    {{ userEditor.form.status === 1 ? 'Enabled' : 'Disabled' }}
                </b-form-checkbox>
            </div>

            <div class="d-flex justify-content-end mt-4">
                <b-button variant="secondary" @click="$refs.userModal.hide()">Cancel</b-button>
                <b-button variant="primary" class="ml-2" @click="saveUserEditor">Save User</b-button>
            </div>
        </b-modal>
    </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { PREFERENCE_DEFAULTS } from '@/utils/preferences';
import ApiServices from '@/services/api';
import dayjs from 'dayjs';
import { phpToDayjs } from '@/utils/dateFormat';

const PREF_DEFAULTS = Object.assign({}, PREFERENCE_DEFAULTS)

export default {
    data() {
        return {
            isEdit: false,
            service_provider_id: null,
            vilogiConfigured: false,
            removingVilogiConfig: false,
            form: {
                name: '',
                meter_allowance: '',
                users: [],
                preferences: Object.assign({}, PREF_DEFAULTS),
                vilogi_api_key: '',
                allow_vilogi_webhook: false
            },
            error: {},
            errorUsers: [],
            emailRegExp: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            errorFlag: false,
            validationMessage: '',
            userEditor: {
                index: null,
                form: {}
            },
            userModalError: {
                name: false,
                email: false,
                role_id: false,
                message: ''
            },
            roleOptions: [
                { value: 2, text: 'Admin' },
                { value: 3, text: 'Surveyor' }
            ],
            emailLanguageOptions: [
                { value: 'en', text: 'English' },
                { value: 'fr', text: 'French' },
                { value: 'es', text: 'Spanish' }
            ],
            timezones: [],
            tzSearch: ''
        };
    },
    props: {
        provider: {
            type: Object,
            default: null
        }
    },
    mounted() {
        if (this.provider) {
            this.isEdit = true;
            this.service_provider_id = this.provider.id;
            this.form.name = this.provider.name;
            this.form.meter_allowance = this.provider.service_provider_mail_config
                ? this.provider.service_provider_mail_config.meter_allowance || ''
                : '';
            this.form.preferences = Object.assign({}, PREF_DEFAULTS, this.provider.preferences || {});
            this.form.allow_vilogi_webhook = !!this.provider.allow_vilogi_webhook;
            this.vilogiConfigured = !!this.provider.vilogi_configured;
            this.form.users = (this.provider.details || []).map(user => ({
                name: user.name || '',
                email: user.email || '',
                password: '',
                confirmPassword: '',
                id: user.id,
                role_id: this.normalizeRoleId(user.role_id),
                status: Number(user.status) === 1 ? 1 : 0
            }));
        }

        this.errorUsers = this.form.users.map(() => ({}));
        this.resetUserEditor();

        // Load IANA timezone list for the timezone picker. Cached at module level after first fetch. 2026-06-17
        ApiServices.getTimezones()
            .then(response => {
                this.timezones = Array.isArray(response.data) ? response.data : response
            })
            .catch(() => {})
    },
    computed: {
        ...mapGetters('serviceProvider', ['serviceProviderList']),
        dateFormatExample() {
            try {
                return dayjs().format(phpToDayjs(this.form.preferences.date_format))
            } catch (e) {
                return ''
            }
        },
        // Filter the IANA timezone list by the search input. 2026-06-17
        filteredTimezones() {
            const q = this.tzSearch.trim().toLowerCase()
            const list = q
                ? this.timezones.filter(tz => tz.toLowerCase().includes(q))
                : this.timezones
            return list.map(tz => ({ value: tz, text: tz }))
        },
        // Build hour select options 0-23 formatted as "12:00 AM", "1:00 PM", etc. 2026-06-17
        hourOptions() {
            return Array.from({ length: 24 }, (_, i) => ({
                value: i,
                text: this.formatHour(i)
            }))
        },
        userEntries() {
            return this.form.users.map((user, index) => ({
                index,
                user
            }));
        },
        adminUserEntries() {
            return this.userEntries.filter(entry => this.normalizeRoleId(entry.user.role_id) === 2);
        },
        surveyorUserEntries() {
            return this.userEntries.filter(entry => this.normalizeRoleId(entry.user.role_id) === 3);
        },
        disabledUserEntries() {
            return this.userEntries.filter(entry => Number(entry.user.status) !== 1);
        }
    },
    methods: {
        ...mapActions('serviceProvider', [
            'ADD_SERVICE_PROVIDER',
            'UPDATE_SERVICE_PROVIDER'
        ]),
        // Format an integer hour 0-23 as a readable 12-hour time string. 2026-06-17
        formatHour(h) {
            const d = new Date(2000, 0, 1, h, 0)
            return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
        },
        createEmptyUser() {
            return {
                name: '',
                email: '',
                password: '',
                confirmPassword: '',
                role_id: 2,
                status: 1
            };
        },
        resetUserEditor() {
            this.userEditor = {
                index: null,
                form: this.createEmptyUser()
            };
            this.userModalError = {
                name: false,
                email: false,
                role_id: false,
                message: ''
            };
        },
        normalizeRoleId(roleId) {
            return Number(roleId) === 3 ? 3 : 2;
        },
        roleLabel(roleId) {
            return this.normalizeRoleId(roleId) === 3 ? 'Surveyor' : 'Admin';
        },
        userInitial(user) {
            return ((user.name || user.email || 'U').charAt(0) || 'U').toUpperCase();
        },
        openUserModal(index = null) {
            const sourceUser = index === null ? this.createEmptyUser() : this.form.users[index];
            this.userEditor = {
                index,
                form: {
                    ...this.createEmptyUser(),
                    ...sourceUser,
                    role_id: this.normalizeRoleId(sourceUser.role_id),
                    status: Number(sourceUser.status) === 1 ? 1 : 0
                }
            };
            this.userModalError = {
                name: false,
                email: false,
                role_id: false,
                message: ''
            };
            this.$refs.userModal.show();
        },
        validateUser(user, index = null) {
            const messagePrefix = index === null ? 'user' : `user ${index + 1}`;
            const errors = {
                name: false,
                email: false,
                role_id: false,
                message: ''
            };

            if (!user.name) {
                errors.name = true;
                errors.message = `Enter a full name for ${messagePrefix}.`;
                return errors;
            }

            if (!this.emailRegExp.test(user.email)) {
                errors.email = true;
                errors.message = `Enter a valid email for ${messagePrefix}.`;
                return errors;
            }

            if (![2, 3].includes(this.normalizeRoleId(user.role_id))) {
                errors.role_id = true;
                errors.message = `Select a valid user type for ${messagePrefix}.`;
                return errors;
            }

            return errors;
        },
        saveUserEditor() {
            const errors = this.validateUser(this.userEditor.form);
            this.userModalError = errors;

            if (errors.message) {
                return;
            }

            if (this.userEditor.index === null) {
                this.form.users.push({
                    ...this.userEditor.form,
                    role_id: this.normalizeRoleId(this.userEditor.form.role_id),
                    status: Number(this.userEditor.form.status) === 1 ? 1 : 0
                });
                this.errorUsers.push({});
            } else {
                const existingUser = this.form.users[this.userEditor.index];
                this.$set(this.form.users, this.userEditor.index, {
                    ...existingUser,
                    ...this.userEditor.form,
                    role_id: this.normalizeRoleId(this.userEditor.form.role_id),
                    status: Number(this.userEditor.form.status) === 1 ? 1 : 0
                });
            }

            this.$refs.userModal.hide();
        },
        removeVilogiConfig() {
            if (!window.confirm('Remove the Vilogi configuration for this provider? This cannot be undone — accounting sync will stop immediately regardless of the toggle above.')) {
                return;
            }
            this.removingVilogiConfig = true;
            ApiServices.deleteVilogiConfig(this.service_provider_id)
                .then(response => {
                    this.vilogiConfigured = false;
                    this.form.vilogi_api_key = '';
                    this.SUCCESS_TOAST_MSG(response.message || 'Vilogi configuration deleted');
                })
                .catch(() => {})
                .finally(() => {
                    this.removingVilogiConfig = false;
                });
        },
        submitForm() {
            this.error = {
                name: false,
                meter_allowance: false
            };
            this.errorFlag = false;
            this.validationMessage = '';
            const validationErrors = [];

            if (!this.form.name) {
                validationErrors.push('Enter a provider name.');
                this.error.name = true;
                this.errorFlag = true;
            }

            if (!this.form.meter_allowance && this.form.meter_allowance !== 0) {
                validationErrors.push('Enter a meter allowance.');
                this.error.meter_allowance = true;
                this.errorFlag = true;
            }

            if (!this.form.users.length) {
                validationErrors.push('Add at least one user.');
                this.errorFlag = true;
            }

            this.errorUsers = [];
            this.form.users.forEach((user, index) => {
                const userErrors = this.validateUser(user, index);
                if (userErrors.message) {
                    validationErrors.push(userErrors.message);
                    this.errorFlag = true;
                }
                this.errorUsers.push(userErrors);
            });

            this.validationMessage = validationErrors[0] || '';

            if (this.errorFlag) return;

            const payload = {
                name: this.form.name,
                details: this.form.users,
                meter_allowance: this.form.meter_allowance,
                preferences: this.form.preferences,
                allow_vilogi_webhook: this.form.allow_vilogi_webhook
            };

            if (this.form.vilogi_api_key) {
                payload.vilogi_api_key = this.form.vilogi_api_key;
            }

            if (this.isEdit) {
                payload.service_provider_id = this.service_provider_id;
                this.UPDATE_SERVICE_PROVIDER(payload).then(() => {
                    this.$router.replace({ name: 'serviceProvider' });
                }).catch(() => { });
            } else {
                this.ADD_SERVICE_PROVIDER(payload).then(() => {
                    this.$router.replace({ name: 'serviceProvider' });
                }).catch(() => { });
            }
        }
    }
};
</script>

<style scoped>
.manage-provider-page .container-fluid {
    max-width: 1320px;
}

.provider-form-card,
.users-section-card {
    border-radius: 1rem;
}

.super-page-kicker {
    font-size: 0.75rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    color: #5e72e4;
}

.provider-summary-badge {
    font-size: 0.85rem;
    padding: 0.65rem 0.9rem;
    background: #eef2ff;
    color: #3d55df;
}

.user-list-grid {
    display: grid;
    gap: 1rem;
}

.user-card {
    padding: 1.1rem 1.25rem;
    border: 1px solid #e7ebf4;
    border-radius: 1rem;
    background: #fff;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.user-card:hover {
    border-color: #cad5f7;
    box-shadow: 0 12px 28px rgba(15, 23, 42, 0.06);
}

.user-card.is-disabled {
    background: #f8f9fc;
    border-color: #edf0f6;
    opacity: 0.72;
}

.user-avatar {
    width: 3rem;
    height: 3rem;
    margin-right: 1rem;
    border-radius: 50%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    color: #3d55df;
    background: #eef2ff;
}

.user-avatar.muted {
    color: #8898aa;
    background: #edf0f6;
}

.user-card-actions {
    gap: 0.5rem;
}

.user-role-label {
    display: inline-block;
    margin-top: 0.2rem;
    font-size: 0.78rem;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #5e72e4;
}

.user-switch {
    margin-bottom: 0;
}

.user-empty-state {
    padding: 2.5rem 1rem;
    text-align: center;
    border: 1px dashed #d6dceb;
    border-radius: 1rem;
    background: #fbfcff;
}

.modal-user-status {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    padding: 1rem;
    border-radius: 0.85rem;
    background: #f8f9fc;
}

.font-weight-semibold {
    font-weight: 600;
}

::v-deep .user-status-tabs .nav-link {
    border-radius: 999px;
    color: #52627a;
    font-weight: 600;
}

::v-deep .user-status-tabs .nav-link.active {
    background: #5e72e4;
    color: #fff;
}

@media (max-width: 991.98px) {
    .modal-user-status {
        flex-direction: column;
        align-items: flex-start;
    }
}
</style>



// WEBPACK FOOTER //
// src/pages/SuperAdmin/manageServiceProvider.vue